import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../context/StateContext";
import { useActionCompany } from "../../../../../hooks/useActionCompany";
import useActionSyncUser from "../../../../../hooks/useSyncUser";
import { Company } from "../../../../../models/company";
import { Error } from "../../../../../models/error";
import CompanyService from "../../../../../services/company.service";
import { keysToCamel } from "../../../../../util/convertCamelCase";
import { FormCompany } from "./FormCompany";
import useStyles from "./styles";
import { checkResourceCan, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { CodeError } from "Util/enums";

const CompanyComponent = () => {
  const { currentUser, userPermissions, setCurrentUser } = useAppState();
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { setToggle } = useActionCompany();

  const { mutate: syncUser } = useActionSyncUser();

  const [companyInformation, setCompanyInformation] = useState<Company | undefined>(undefined);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [companyPermission, setCompanyPermission] = useState<{
    edit: boolean;
  }>({
    edit: false,
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setCompanyPermission({
        edit: checkResourceCan(userPermissions, ResourceName.COMPANY, ResourceCanEnum.EDIT),
      });
    }
  }, [userPermissions]);

  const checkValidUrl = (url: string): boolean => {
    let urlCheck: string = url;

    if (url.includes("www")) [, urlCheck] = url.split("www.");

    if (urlCheck && urlCheck.includes(".")) {
      const urlAux = urlCheck.split(".");

      if (urlAux[0].length > 0) urlCheck = urlAux[urlAux.length - 1];
      else return false;
    } else return false;

    if (urlCheck.length >= 2 && urlCheck.length <= 3) return true;

    return false;
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    webSite: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("check-url", "This field is an invalid url", (webSite) =>
        webSite ? checkValidUrl(webSite) : false
      ),
    phone: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    email: yup
      .string()
      .email()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    registrationNumber: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .matches(/^[a-zA-Z0-9]+$/, translate("thisFieldShouldHaveAlphaNumericCharactersOnly")),
  });

  const { isFetching: loading, refetch: refetchCompany } = useQuery(
    ["company"],
    () =>
      CompanyService.getById(Number(currentUser?.companyId), {
        include: JSON.stringify([
          { association: "location" },
          { association: "logo", include: [{ association: "resizes" }] },
          { association: "icon" },
          {
            association: "contacts",
            include: [
              {
                association: "user",
                include: [
                  { association: "picture" },
                  { association: "manager" },
                  { association: "location" },
                  { association: "timezone" },
                ],
              },
            ],
            required: false,
            where: { valid: true, customerId: null },
          },
          { association: "termsAcceptedUser" },
          { association: "timezone" },
          { association: "companySubdomain" },
        ]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setCompanyInformation(response.data);
        }
      },
    }
  );

  const { mutate: updateCompany, isLoading: isUpdating } = useMutation(
    ["company.update"],
    ({ id, company }: { id: number; company: Company }) => CompanyService.update(id, company),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.update.company",
        });
        setToggle(true);
        syncUser();
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) {
          if (
            err?.response?.data?.field === "subdomain" &&
            err?.response?.data?.error === CodeError.NOT_AVAILABLE
          ) {
            return NotificationToast.error(
              translate("thisSubdomainIsNotAvailablePleaseChooseADifferentOne")
            );
          }

          NotificationToast.error(err?.response?.data?.message);
        }
      },
    }
  );

  const handleValidationSuccess = (company: Company): boolean => {
    schema.validateSync(
      {
        name: company?.name,
        webSite: company?.webSite,
        phone: company?.phone,
        email: company?.email,
        registrationNumber: company?.registrationNumber,
      },
      { abortEarly: false }
    );

    setErrors([]);

    return true;
  };

  const handleValidationError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const handleValidateForm = (company: Company): boolean => {
    try {
      return handleValidationSuccess(company);
    } catch (error) {
      return handleValidationError(error);
    }
  };

  const handleSubmit = () => {
    setIsSubmitted(true);

    if (companyInformation) {
      if (handleValidateForm(companyInformation)) {
        if (companyInformation?.id) {
          updateCompany({
            id: companyInformation.id,
            company: companyInformation,
          });

          if (currentUser) {
            setCurrentUser({
              ...currentUser,
              company: {
                ...currentUser.company,
                isMilitary: companyInformation.isMilitary,
              },
            });
          }
        }
      }
    }
  };

  useEffect(() => {
    if (isSubmitted && companyInformation) handleValidateForm(companyInformation);
  }, [isSubmitted, companyInformation]);

  return (
    <Grid container direction="row" className={classes.generalWrapper}>
      <FormCompany
        refetchCompany={() => refetchCompany()}
        companyInformation={keysToCamel(companyInformation || [])}
        update={(company: Company) => setCompanyInformation(company)}
        errors={errors}
        handleSubmit={handleSubmit}
        loading={loading}
        isUpdating={isUpdating}
        permission={{
          edit: isShowAction(currentUser, companyPermission.edit),
        }}
      />
    </Grid>
  );
};

export default CompanyComponent;
